/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { ReactElement } from "react";
import uuid from "react-uuid";

import { Dayjs } from "dayjs";
import moment from "moment";

import { TFunction, gentlyTruncateString } from "utils";

import {
  CoverCropWithScores,
  CropRecommendationParametersRequest,
  CropRecommendationResponse,
  INVESTMENT_LEVEL,
  MAIN_CROP_CREATION_MODAL_TYPE,
  MainCropOption,
  MainCropsResponse,
  NavigationSplitViewOnTileClickedProps,
  PH_TYPE,
  SOIL_TYPE,
} from "models";

import { Flex, Icon, Link, Popover, TooltipWrapper } from "components";

import { MainCropUpdateForm } from "pages/app/digital-agronomist/components/MainCropUpdateForm";
import { OnboardingStep } from "pages/app/digital-agronomist/components/Onboarding";
import { Preference } from "pages/app/digital-agronomist/components/Preferences.tsx";
import { UpdatePreferencesStep } from "pages/app/digital-agronomist/components/UpdateDigitalAgronomistPreferencesForm";

const MAX_DISPLAY_TAB_NAME_LENGTH = 20;

type MainCrop = { year: number; mainCropName: string; seedingDate: string; harvestDate: string; nitrogenNeed: boolean };

export const getValueTranslationKey = (
  fieldName: keyof OnboardingFormValues,
  value: OnboardingFormValues[keyof OnboardingFormValues],
  steps: OnboardingStep[] | UpdatePreferencesStep[]
) => {
  const stepOptions = steps.find((step) => step.name === fieldName)?.options;
  const translationKey = stepOptions?.find((option) => option.value === value)?.translationKey;
  return translationKey;
};

export const convertResponseToPreference = (
  response: CropRecommendationResponse | null,
  t: (key: string) => string,
  handlePreferenceClick: (position: NavigationSplitViewOnTileClickedProps) => void
): Preference[] => {
  if (!Object.keys(response || {}).length) {
    return [];
  }
  const preferences: Preference[] = [];

  // Farm profile
  if (response?.preferences.isDrought != null)
    preferences.push({
      name: t("onboarding.steps.isDrought.shortTitle"),
      value: response.preferences.isDrought ? t("onboarding.options.yes") : t("onboarding.options.no"),
      position: { groupIndex: 0, subGroupIndex: 0, tileIndex: 0 },
      onClick: handlePreferenceClick,
    });
  if (response?.preferences.pHType != null)
    preferences.push({
      name: t("onboarding.steps.pHType.shortTitle"),
      value: t(`onboarding.options.${response.preferences.pHType.toLowerCase()}`),
      position: { groupIndex: 0, subGroupIndex: 0, tileIndex: 1 },
      onClick: handlePreferenceClick,
    });
  if (response?.preferences.soilType != null)
    preferences.push({
      name: t("onboarding.steps.soilType.shortTitle"),
      value: t(`onboarding.options.${response.preferences.soilType.toLowerCase()}`),
      position: { groupIndex: 0, subGroupIndex: 0, tileIndex: 2 },
      onClick: handlePreferenceClick,
    });

  // Crop rotation preferences
  if (response?.preferences.legumeBreaksYears != null)
    preferences.push({
      name: t("onboarding.steps.legumeBreaksYears.shortTitle"),
      value: response.preferences.legumeBreaksYears.toString(),
      position: { groupIndex: 0, subGroupIndex: 1, tileIndex: 0 },
      onClick: handlePreferenceClick,
    });
  if (response?.preferences.investmentLevel != null)
    preferences.push({
      name: t("onboarding.steps.investmentLevel.shortTitle"),
      value: t(`onboarding.options.${response.preferences.investmentLevel.toLowerCase()}`),
      position: { groupIndex: 0, subGroupIndex: 1, tileIndex: 1 },
      onClick: handlePreferenceClick,
    });
  if (response?.preferences.isFodder != null)
    preferences.push({
      name: t("onboarding.steps.isFodder.shortTitle"),
      value: response.preferences.isFodder ? t("onboarding.options.yes") : t("onboarding.options.no"),
      position: { groupIndex: 0, subGroupIndex: 1, tileIndex: 2 },
      onClick: handlePreferenceClick,
    });
  if (response?.preferences.isBiogas != null)
    preferences.push({
      name: t("onboarding.steps.isBiogas.shortTitle"),
      value: response.preferences.isBiogas ? t("onboarding.options.yes") : t("onboarding.options.no"),
      position: { groupIndex: 0, subGroupIndex: 1, tileIndex: 3 },
      onClick: handlePreferenceClick,
    });

  return preferences;
};

// Utiles function for Main Crops table
export const generateMainCropsColumns = (
  cropRecommendations: CropRecommendationResponse | undefined,
  mainCropsList: MainCropOption[],
  mainCropYearToAddOrUpdate: number | null,
  setMainCropYearToAddOrUpdate: (year: number | null) => void,
  mainCropsNumber: number
) => {
  if (!cropRecommendations) return [];

  const columns: { title: string | ReactElement; dataIndex: string; key: string; width?: string | number }[] = [
    {
      title: "",
      dataIndex: "emptyColumnHeader",
      key: "emptyColumnHeader",
      width: 200,
    },
  ];

  const years = cropRecommendations.cropRecommendations.map((cropRecommendation) => cropRecommendation.mainCrop.year);

  years.forEach((year, i) => {
    const mainCrop = cropRecommendations.cropRecommendations.find((recommendation) => recommendation.mainCrop.year === year)?.mainCrop;

    columns.push({
      title: (
        <Flex align="center" justify="space-between">
          <Popover
            className="digital-agronomist-main-crop-update"
            content={
              <MainCropUpdateForm
                mainCropsList={mainCropsList}
                preferenceId={cropRecommendations.id}
                mainCropDetails={mainCrop as MainCropsResponse}
                showDelete={i > 1 && i === years.length - 1}
                onClose={() => setMainCropYearToAddOrUpdate(null)}
                creationType={MAIN_CROP_CREATION_MODAL_TYPE.UPDATE}
                mainCropsNumber={mainCropsNumber}
              />
            }
            trigger="click"
            placement="left"
            open={year === mainCropYearToAddOrUpdate}
            onOpenChange={() => {
              setMainCropYearToAddOrUpdate(mainCropYearToAddOrUpdate === null ? year : null);
            }}
            destroyOnHidden={true}
          >
            <Flex align="center" gap=".5rem">
              <p className="paragraph-m-bold margin-0">{year}</p>
              <Icon className="pointer" size="s" iconName="klim/edit-element" />
            </Flex>
          </Popover>
          {i === years.length - 1 && years.length < 5 ? (
            <Popover
              className="digital-agronomist-main-crop-update"
              content={
                <MainCropUpdateForm
                  mainCropsList={mainCropsList}
                  preferenceId={cropRecommendations.id}
                  mainCropDetails={{ year: years[years.length - 1] + 1 } as MainCropsResponse}
                  showDelete={false}
                  onClose={() => setMainCropYearToAddOrUpdate(null)}
                  creationType={MAIN_CROP_CREATION_MODAL_TYPE.ADD}
                  mainCropsNumber={mainCropsNumber}
                />
              }
              trigger="click"
              placement="left"
              open={year + 1 === mainCropYearToAddOrUpdate}
              onOpenChange={() => {
                setMainCropYearToAddOrUpdate(mainCropYearToAddOrUpdate === null ? years[years.length - 1] + 1 : null);
              }}
              destroyOnHidden={true}
            >
              <Icon className="pointer add-year" iconName="klim/plus" dataTestId="da-add-year" />
            </Popover>
          ) : (
            <></>
          )}
        </Flex>
      ),
      dataIndex: year.toString(),
      key: year.toString(),
    });
  });

  return columns;
};

const generateDataSourceForMainCrops = (mainCrops: MainCrop[], t: TFunction) => {
  const data: any = [];

  // The number of row items we need with the key being the value for the "emptyColumnHeader" property
  const columns = Object.keys(mainCrops[0]).filter((column) => column !== "year");

  columns.forEach((column) => {
    data.push({ emptyColumnHeader: column, key: uuid() });
  });

  mainCrops.forEach((mainCrop) => {
    data.forEach((data: any) => {
      Object.entries(mainCrop).forEach(([key, value]) => {
        if (data.emptyColumnHeader !== key) return;

        if (key === "nitrogenNeed") {
          if (value) {
            data[mainCrop.year] = t("table.yes");
          } else {
            data[mainCrop.year] = t("table.no");
          }
        } else {
          data[mainCrop.year] = (
            <TooltipWrapper key={uuid()} tooltip={value.toString().length > MAX_DISPLAY_TAB_NAME_LENGTH ? { title: value as string, placement: "top" } : {}}>
              <p className="paragraph-m margin-0">{gentlyTruncateString(value, MAX_DISPLAY_TAB_NAME_LENGTH)}</p>
            </TooltipWrapper>
          );
        }
      });
    });
  });

  // Add translated titles to the data items
  return data.map((item: any) => ({ ...item, emptyColumnHeader: t(`table.${item.emptyColumnHeader}`) }));
};
export const convertToMainCropData = (cropRecommendationsResponse: CropRecommendationResponse | undefined, t: TFunction) => {
  if (!cropRecommendationsResponse) {
    return [];
  }
  const mainCrops = cropRecommendationsResponse.cropRecommendations.map((recommendation) => ({
    year: recommendation.mainCrop.year,
    mainCropName: recommendation.mainCrop.mainCropName,
    seedingDate: moment(recommendation.mainCrop.seedingDate).format("DD.MM.YY"),
    harvestDate: moment(recommendation.mainCrop.harvestDate).format("DD.MM.YY"),
    nitrogenNeed: recommendation.nitrogenNeed,
  }));

  return generateDataSourceForMainCrops(mainCrops, t);
};

// Utiles function for Cover Crops table
export const generateCoverCropsColumns = (cropRecommendations: CropRecommendationResponse | undefined, t: TFunction) => {
  if (!cropRecommendations) return [];

  const columns: {
    title: string | ReactElement;
    dataIndex: string;
    key: string;
    width?: string | number;
  }[] = [
    {
      title: "",
      dataIndex: "emptyColumnHeader",
      key: "emptyColumnHeader",
      width: 200,
    },
  ];

  const years = cropRecommendations?.cropRecommendations.map((cropRecommendation) => cropRecommendation.mainCrop.year);

  years.forEach((year, index) => {
    columns.push({
      title: (
        <Flex align="center" justify="flex-start">
          <p className="paragraph-m-bold margin-0">{`${year} - ${year + 1}`}</p>
          {index === years.length - 1 && (
            <TooltipWrapper tooltip={{ title: t("recommendationTable.info"), placement: "top" }} style={{ display: "flex", alignItems: "center" }}>
              <Icon className="margin-left-25" size="s" iconName="tabler/info-circle" />
            </TooltipWrapper>
          )}
        </Flex>
      ),
      dataIndex: year.toString(),
      key: year.toString(),
    });
  });

  return columns;
};

export const transformCoverCropsResponseToTableData = (
  response: CropRecommendationResponse | undefined,
  t: TFunction,
  handleDrawerClick: (coverCropWithScores: CoverCropWithScores) => void
) => {
  if (!response) return [];

  // Get the crop recommendations array
  const cropRecommendations = response.cropRecommendations.map((recommendation) => ({
    ...recommendation,
    // Sort the cover crops by score in descending order (highest to lowest)
    coverCropsWithScores: recommendation.coverCropsWithScores.sort((a, b) => b.coverCropScore - a.coverCropScore),
  }));

  // Get the maximum number of cover crops in any recommendation
  const maxCoverCrops = Math.max(...cropRecommendations.map((rec) => rec.coverCropsWithScores.length));

  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  const tableSchema: any[] = [];

  // Loop through the number of cover crops
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < maxCoverCrops; i++) {
    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    const row: any = {
      emptyColumnHeader: <p className="paragraph-m-bold margin-0">{t("recommendationTable.coverCropName", { count: i + 1 })}</p>,
      key: i,
      children: [],
    };

    cropRecommendations.forEach((recommendation) => {
      const { year } = recommendation.mainCrop;

      if (recommendation.coverCropsWithScores[i]) {
        row[year] = (
          <TooltipWrapper
            key={uuid()}
            tooltip={
              recommendation.coverCropsWithScores[i].coverCropName.length > MAX_DISPLAY_TAB_NAME_LENGTH
                ? { title: recommendation.coverCropsWithScores[i].coverCropName, placement: "top" }
                : {}
            }
          >
            <Link onClick={() => handleDrawerClick(recommendation.coverCropsWithScores[i])} className="margin-0">
              {gentlyTruncateString(recommendation.coverCropsWithScores[i].coverCropName, MAX_DISPLAY_TAB_NAME_LENGTH)}
            </Link>
          </TooltipWrapper>
        );
      }

      // Add children rows for cover crop score and seeding date for each year
      if (!row.children.length) {
        row.children.push(
          {
            key: uuid(),
            emptyColumnHeader: (
              <p key={uuid()} className="paragraph-m margin-0 padding-left-250" style={{ color: "#6D6D6D" }}>
                {t("recommendationTable.score")}
              </p>
            ),
            [year]: recommendation.coverCropsWithScores[i]?.coverCropScore ? (
              <p key={uuid()} className="paragraph-m margin-0" style={{ color: "#6D6D6D" }}>
                {recommendation.coverCropsWithScores[i]?.coverCropScore}%
              </p>
            ) : null,
          },
          {
            key: uuid(),
            emptyColumnHeader: (
              <p key={uuid()} className="paragraph-m margin-0" style={{ color: "#6D6D6D" }}>
                {t("recommendationTable.seedingDate")}
              </p>
            ),
            [year]: recommendation.coverCropsWithScores[i]?.coverCropScore ? (
              <p key={uuid()} className="paragraph-m margin-0" style={{ color: "#6D6D6D" }}>
                {moment(recommendation.mainCrop.harvestDate).format("DD.MM.YY")}
              </p>
            ) : null,
          }
        );
      } else {
        row.children[0][year] = recommendation.coverCropsWithScores[i]?.coverCropScore ? (
          <p key={uuid()} className="paragraph-m margin-0" style={{ color: "#6D6D6D" }}>
            {recommendation.coverCropsWithScores[i]?.coverCropScore}%
          </p>
        ) : null;
        row.children[1][year] = recommendation.coverCropsWithScores[i]?.coverCropScore ? (
          <p key={uuid()} className="paragraph-m margin-0" style={{ color: "#6D6D6D" }}>
            {moment(recommendation.mainCrop.harvestDate).format("DD.MM.YY")}
          </p>
        ) : null;
      }
    });

    // Push the row into the table schema
    tableSchema.push(row);
  }

  // Add the main crop name row to the top of the table schema
  const mainCropNameRow: any = { key: uuid(), emptyColumnHeader: <p className="paragraph-m-bold margin-0">{t("recommendationTable.mainCropSequence")}</p> };
  cropRecommendations.forEach((recommendation, index) => {
    const isLast = index === cropRecommendations.length - 1;
    const nextMainCropName = isLast ? cropRecommendations[0].mainCrop.mainCropName : cropRecommendations[index + 1]?.mainCrop.mainCropName;
    const currentMainCropName = `${recommendation.mainCrop.mainCropName} ${nextMainCropName ? `- ${nextMainCropName}` : ""}`;
    mainCropNameRow[recommendation.mainCrop.year] = (
      <TooltipWrapper key={uuid()} tooltip={currentMainCropName.length > MAX_DISPLAY_TAB_NAME_LENGTH ? { title: currentMainCropName, placement: "top" } : {}}>
        <p className="paragraph-m margin-0">{gentlyTruncateString(currentMainCropName, MAX_DISPLAY_TAB_NAME_LENGTH)}</p>
      </TooltipWrapper>
    );
  });

  tableSchema.unshift(mainCropNameRow);

  return tableSchema;
};

// Form
export type MainCropDetails = {
  cropId: string;
  sowingDate: Dayjs;
  harvestDate: Dayjs;
};

export type OnboardingFormValues = {
  isDrought?: boolean;
  pHType?: PH_TYPE;
  soilType?: SOIL_TYPE;
  legumeBreaksYears?: number;
  investmentLevel?: INVESTMENT_LEVEL;
  isFodder?: boolean;
  isBiogas?: boolean;
  mainCropsNumber?: number;
  mainCrop1?: MainCropDetails;
  mainCrop2?: MainCropDetails;
  mainCrop3?: MainCropDetails;
  mainCrop4?: MainCropDetails;
  mainCrop5?: MainCropDetails;
};

export type EditPreferencesFormValues = {
  isDrought?: boolean;
  pHType?: PH_TYPE;
  soilType?: SOIL_TYPE;
  legumeBreaksYears?: number;
  investmentLevel?: INVESTMENT_LEVEL;
  isFodder?: boolean;
  isBiogas?: boolean;
};

export const joinStringValues = (values: (string | number)[]): string => values.filter(Boolean).join(" ");

export const convertStepValueToLabel = (
  t: (key: string) => string,
  fieldName: keyof OnboardingFormValues | keyof EditPreferencesFormValues,
  value?: OnboardingFormValues[keyof OnboardingFormValues],
  translationKey?: string,
  prefix: string = "",
  suffix: string = ""
): string | undefined => {
  if (value === undefined || value === null) return;
  if (typeof value === "boolean") return value ? t("onboarding.options.yes") : t("onboarding.options.no");

  let fullValue;
  switch (fieldName) {
    case "legumeBreaksYears":
      fullValue =
        value === 0
          ? t("onboarding.options.notApplicable")
          : value === 1
          ? joinStringValues(["≤", value, t("onboarding.options.year")])
          : value === 5
          ? joinStringValues(["≥", value, t("onboarding.options.years")])
          : joinStringValues([value as string, t("onboarding.options.years")]);
      break;
    case "mainCropsNumber":
      fullValue = value === 1 ? joinStringValues([value, t("onboarding.options.crop")]) : joinStringValues([value as string, t("onboarding.options.crops")]);
      break;
    default:
      fullValue = translationKey
        ? joinStringValues([prefix, t(`onboarding.options.${translationKey}`), suffix])
        : joinStringValues([prefix, value as string, suffix]);
  }

  return fullValue.toLowerCase();
};

export enum FORM_ITEM_TYPE {
  RADIO = "radio",
  SELECT = "select",
  MAIN_CROP = "mainCrop",
}

export const mainCropFieldNameRegexTester = /^mainCrop\d+$/;

export const generatePreferenceRequestBody = (formValues: OnboardingFormValues): CropRecommendationParametersRequest => {
  const settingsPreferences = {};
  const mainCropsPreferences: CropRecommendationParametersRequest["mainCropsPreferences"] = [];

  Object.entries(formValues).forEach(([key, value]) => {
    if (mainCropFieldNameRegexTester.test(key)) {
      const mainCrop = {
        mainCropId: (value as MainCropDetails).cropId,
        seedingDate: (value as MainCropDetails).sowingDate.format(),
        harvestDate: (value as MainCropDetails).harvestDate.format(),
        year: (value as MainCropDetails).harvestDate.year(),
      };
      mainCropsPreferences.push(mainCrop);
      return;
    }

    Object.assign(settingsPreferences, { [key]: value });
  });

  return {
    settingsPreferences: settingsPreferences,
    mainCropsPreferences: mainCropsPreferences,
  } as CropRecommendationParametersRequest;
};
